import React, { useState } from 'react';
import Tooltip from '../common/Tooltip';

/**
 * KnowledgeBaseList - Shared component for displaying Knowledge Base articles
 * 
 * Used by:
 * - ChatbotAdminPage (Knowledge tab)
 * - KnowledgeManagementPage (Knowledge Base tab)
 * 
 * Props:
 * - knowledge: Array of knowledge entries
 * - filteredKnowledge: Filtered/sorted knowledge entries
 * - categories: Array of category strings
 * - searchQuery: Current search query
 * - setSearchQuery: Setter for search query
 * - sourceFilter: Current source filter value
 * - setSourceFilter: Setter for source filter
 * - categoryFilter: Current category filter value
 * - setCategoryFilter: Setter for category filter
 * - statusFilter: Current status filter value
 * - setStatusFilter: Setter for status filter
 * - sortBy: Current sort field
 * - setSortBy: Setter for sort field
 * - articleSatisfaction: Object with article satisfaction metrics (optional)
 * - onEdit: Function to call when editing an entry
 * - onDelete: Function to call when deleting an entry
 * - onToggleActive: Function to call when toggling active status
 * - onMarkForReview: Function to call when flagging an entry for review (available to any user)
 */
const KnowledgeBaseList = ({
  knowledge = [],
  filteredKnowledge = [],
  categories = [],
  searchQuery = '',
  setSearchQuery,
  sourceFilter = 'all',
  setSourceFilter,
  categoryFilter = 'all',
  setCategoryFilter,
  statusFilter = 'active',
  setStatusFilter,
  sortBy = 'priority',
  setSortBy,
  articleSatisfaction = {},
  onEdit,
  onDelete,
  onToggleActive,
  onMarkForReview
}) => {
  const [selectedEntry, setSelectedEntry] = useState(null);

  return (
    <div>
      {/* Search filter */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search knowledge base..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 transition-colors ${
            searchQuery ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
          }`}
        />
      </div>
      
      {/* Filters and Sort */}
      <div className="mb-4 flex flex-wrap gap-3 items-center">
        {/* Source Filter */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Source</label>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Sources</option>
            <option value="faq">📋 FAQ</option>
            <option value="manual">✏️ Manual</option>
            <option value="ai-feedback">🤖 AI Feedback</option>
          </select>
        </div>
        
        {/* Category Filter */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Category</label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        
        {/* Status Filter */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="needs-review">Needs Review</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        
        {/* Sort */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Sort By</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="priority">Priority (High to Low)</option>
            <option value="title">Title (A-Z)</option>
            <option value="createdAt">Created (Newest)</option>
            <option value="updatedAt">Updated (Recent)</option>
          </select>
        </div>
        
        {/* Results count */}
        <div className="ml-auto text-sm text-gray-600">
          {filteredKnowledge.length} of {knowledge.length} articles
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full table-fixed divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-[26%] px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Article Title</th>
              <th className="w-[8%] px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Category</th>
              <th className="w-[6%] px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Source</th>
              <th className="w-[6%] px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Priority</th>
              <th className="w-[8%] px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                <Tooltip text="Satisfaction rate based on feedback for responses that used this specific article">
                  <span className="cursor-help border-b border-dashed border-gray-400">Satisfaction</span>
                </Tooltip>
              </th>
              <th className="w-[10%] px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="w-[12%] px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Dates</th>
              <th className="w-[14%] px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredKnowledge
              .slice(0, 100)
              .map(entry => (
              <React.Fragment key={entry.id}>
                <tr 
                  className={`hover:bg-gray-50 cursor-pointer ${selectedEntry?.id === entry.id ? 'bg-indigo-50' : ''}`}
                  onClick={() => setSelectedEntry(selectedEntry?.id === entry.id ? null : entry)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-gray-400 flex-shrink-0">{selectedEntry?.id === entry.id ? '▼' : '▶'}</span>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate" title={entry.title}>
                          {entry.title}
                        </div>
                        {entry.steps && entry.steps.length > 0 && (
                          <span className="text-xs text-green-600">{entry.steps.length} steps</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                      {entry.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      entry.source === 'faq' ? 'bg-green-100 text-green-700' :
                      entry.source === 'manual' ? 'bg-purple-100 text-purple-700' :
                      entry.source === 'ai-feedback' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {entry.source === 'ai-feedback' ? 'AI' : entry.source}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{entry.priority}</td>
                  <td className="px-4 py-3">
                    {articleSatisfaction[entry.id] ? (
                      <Tooltip text={`${articleSatisfaction[entry.id].helpful} helpful / ${articleSatisfaction[entry.id].total} total responses using this article`}>
                        <div className="flex items-center gap-1">
                          <div className={`text-sm font-medium ${
                            (articleSatisfaction[entry.id].helpful / articleSatisfaction[entry.id].total) >= 0.7 
                              ? 'text-green-600' 
                              : (articleSatisfaction[entry.id].helpful / articleSatisfaction[entry.id].total) >= 0.4
                                ? 'text-amber-600'
                                : 'text-red-600'
                          }`}>
                            {((articleSatisfaction[entry.id].helpful / articleSatisfaction[entry.id].total) * 100).toFixed(0)}%
                          </div>
                          <span className="text-xs text-gray-400">({articleSatisfaction[entry.id].total})</span>
                        </div>
                      </Tooltip>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const s = entry.status || (entry.isActive !== false ? 'published' : 'archived');
                      const today = new Date().toISOString().split('T')[0];
                      const overdue = entry.reviewDate && entry.reviewDate <= today;
                      const expiring = entry.expiresAt && entry.expiresAt <= today;
                      return (
                        <div className="flex flex-col gap-0.5">
                          <span className={`px-2 py-1 text-xs rounded-full inline-block w-fit ${
                            s === 'published' ? 'bg-green-100 text-green-700' :
                            s === 'draft' ? 'bg-gray-200 text-gray-600' :
                            s === 'needs-review' ? 'bg-amber-100 text-amber-700' :
                            s === 'archived' ? 'bg-red-100 text-red-600' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {s === 'published' ? 'Published' : s === 'draft' ? 'Draft' : s === 'needs-review' ? 'Review' : s === 'archived' ? 'Archived' : s}
                          </span>
                          {overdue && (
                            <span className="text-xs text-amber-600 font-medium">Review due</span>
                          )}
                          {expiring && (
                            <span className="text-xs text-red-600 font-medium">Expired</span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const today = new Date().toISOString().split('T')[0];
                      const updated = entry.updatedAt?.toDate?.();
                      return (
                        <div className="flex flex-col gap-0.5 text-xs text-gray-500">
                          {updated && (
                            <span>Updated: {updated.toLocaleDateString()}</span>
                          )}
                          {entry.reviewDate ? (
                            <span className={entry.reviewDate <= today ? 'text-orange-600 font-medium' : ''}>
                              Review: {entry.reviewDate}
                            </span>
                          ) : entry.status !== 'draft' && entry.status !== 'archived' ? (
                            <span className="text-indigo-500 italic">No review date</span>
                          ) : null}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Flag for Review - available to any user */}
                      {onMarkForReview && entry.status !== 'needs-review' && entry.status !== 'archived' && entry.status !== 'draft' && (
                        <Tooltip text="Flag this article for admin review">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onMarkForReview(entry);
                            }}
                            className="text-xs px-2 py-1 rounded text-amber-700 hover:bg-amber-50"
                          >
                            Flag
                          </button>
                        </Tooltip>
                      )}
                      {/* Edit button - hidden for read-only */}
                      {onEdit && (
                        <Tooltip text={entry.source === 'faq' ? 'Edit (override - re-sync will restore original)' : 'Edit entry'}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(entry);
                            }}
                            className={`text-xs px-2 py-1 rounded ${
                              entry.source === 'faq' 
                                ? 'text-purple-600 hover:bg-purple-50' 
                                : 'text-blue-600 hover:bg-blue-50'
                            }`}
                          >
                            Edit
                          </button>
                        </Tooltip>
                      )}
                      {/* Toggle active */}
                      {onToggleActive && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleActive(entry.id, entry.isActive !== false);
                          }}
                          className={`text-xs px-2 py-1 rounded ${
                            entry.isActive !== false
                              ? 'text-amber-600 hover:bg-amber-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                        >
                          {entry.isActive !== false ? 'Disable' : 'Enable'}
                        </button>
                      )}
                      {/* Delete button - hidden for read-only */}
                      {onDelete && (
                        <Tooltip text="Delete entry">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(entry);
                            }}
                            className="text-xs px-2 py-1 rounded text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Inline Detail Row */}
                {selectedEntry?.id === entry.id && (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 bg-indigo-50 border-t border-indigo-100">
                      <div className="ml-6">
                        <p className="text-sm text-gray-700 mb-3">{entry.content}</p>
                        {entry.steps && entry.steps.length > 0 && (
                          <div className="mb-3 p-3 bg-white rounded border">
                            <div className="text-xs font-medium text-gray-500 mb-2">Steps ({entry.steps.length}):</div>
                            <ol className="list-decimal list-inside space-y-1 text-sm">
                              {entry.steps.map((step, i) => (
                                <li key={i} className="text-gray-700">{step}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                        {entry.keywords && entry.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            <span className="text-xs text-gray-500 mr-1">Keywords:</span>
                            {entry.keywords.map((kw, i) => (
                              <span key={i} className="px-2 py-0.5 bg-white text-gray-600 text-xs rounded border">{kw}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {knowledge.length > 100 && (
          <div className="p-4 bg-gray-50 text-center text-sm text-gray-600">
            Showing first 100 entries. Use search to find specific entries.
          </div>
        )}
        {filteredKnowledge.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No knowledge articles found matching your filters.
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBaseList;
